import React, { useEffect, useState } from "react";
import { Loader2, Send, Save, Bot, MessageSquare, ExternalLink, Mail, Copy, Search, KeyRound } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import api from "../lib/api";

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [token, setToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [alertEmail, setAlertEmail] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");

  useEffect(() => {
    Promise.all([api.getSettings(), api.getMe()]).then(([s, u]) => {
      setToken(s.telegram_bot_token || "");
      setChatId(s.telegram_chat_id || "");
      setEnabled(!!s.alerts_enabled);
      setEmailEnabled(!!u.email_alerts_enabled);
      setUserEmail(u.email || "");
      setAlertEmail(u.alert_email || "");
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveSettings({
        telegram_bot_token: token || null,
        telegram_chat_id: chatId || null,
        alerts_enabled: enabled,
        email_alerts_enabled: emailEnabled,
      });
      await api.updateUserPreferences({ email_alerts_enabled: emailEnabled, alert_email: alertEmail || null });
      toast.success("Einstellungen gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.saveSettings({
        telegram_bot_token: token || null,
        telegram_chat_id: chatId || null,
      });
      await api.testTelegram();
      toast.success("Test-Nachricht gesendet. Bitte Telegram prüfen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fehlgeschlagen.");
    } finally { setTesting(false); }
  };

  const testEmail = async () => {
    setTestingEmail(true);
    try {
      await api.testEmail();
      toast.success(`Test-E-Mail an ${userEmail} gesendet.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fehlgeschlagen.");
    } finally { setTestingEmail(false); }
  };

  const changePassword = async () => {
    if (pw.length < 6) { toast.error("Mindestens 6 Zeichen."); return; }
    if (pw !== pw2) { toast.error("Passwörter stimmen nicht überein."); return; }
    setChangingPw(true);
    try {
      await api.changeOwnPassword(pw);
      setPw(""); setPw2("");
      toast.success("Passwort geändert.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Ändern.");
    } finally { setChangingPw(false); }
  };

  const detect = async () => {
    if (!token) { toast.error("Bitte zuerst Bot-Token eingeben und speichern."); return; }
    setDetecting(true);
    try {
      // Save token first to ensure backend has latest
      await api.saveSettings({ telegram_bot_token: token });
      const r = await api.detectChatId();
      const chats = r.chats || [];
      if (chats.length === 1) {
        setChatId(chats[0].chat_id);
        toast.success(`Chat-ID gefunden: ${chats[0].chat_id} (${chats[0].name || chats[0].type})`);
      } else if (chats.length > 1) {
        const first = chats[0];
        setChatId(first.chat_id);
        toast.success(`${chats.length} Chats gefunden. Erste übernommen: ${first.chat_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erkennung fehlgeschlagen.");
    } finally { setDetecting(false); }
  };

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto" data-testid="settings-page">
      <div className="font-display text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Konfiguration</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mb-2">
        Einstellungen
      </h1>
      <p className="text-zinc-400 text-sm mb-8">Telegram und E-Mail Alerts konfigurieren.</p>

      {/* Telegram setup */}
      <div className="border border-zinc-800 bg-zinc-950 mb-4">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-3">
          <Bot size={18} className="text-emerald-500" />
          <div>
            <div className="font-display font-bold tracking-tight">Telegram Bot</div>
            <div className="text-xs text-zinc-500">Empfange Alerts wenn Zonen erreicht werden.</div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Bot Token</Label>
              <a
                href="https://t.me/BotFather"
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-zinc-500 hover:text-emerald-400 inline-flex items-center gap-1"
              >
                @BotFather <ExternalLink size={10} />
              </a>
            </div>
            <Input
              data-testid="input-bot-token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="123456789:ABC-DEF..."
              className="bg-zinc-900 border-zinc-800 font-mono-num text-sm"
            />
            <p className="text-[10px] text-zinc-600 mt-1">
              Erstelle einen Bot über @BotFather mit /newbot um deinen Token zu erhalten.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Chat ID</Label>
              <a
                href="https://t.me/userinfobot"
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-zinc-500 hover:text-emerald-400 inline-flex items-center gap-1"
              >
                @userinfobot <ExternalLink size={10} />
              </a>
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="input-chat-id"
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="987654321"
                className="bg-zinc-900 border-zinc-800 font-mono-num text-sm"
              />
              <Button
                onClick={detect}
                disabled={detecting || !token}
                variant="outline"
                className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-xs whitespace-nowrap"
                data-testid="btn-detect-chat-id"
              >
                {detecting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Search size={14} className="mr-1" />}
                Auto-Erkennung
              </Button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-1">
              Schreibe deinem Bot in Telegram <span className="text-emerald-400 font-mono-num">/start</span>, dann klicke "Auto-Erkennung" — die Chat-ID wird automatisch ausgelesen.
            </p>
          </div>

          <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
            <div>
              <div className="font-display font-bold text-sm">Alerts aktivieren</div>
              <div className="text-xs text-zinc-500">Sende automatisch Nachrichten bei Zonen-Treffer.</div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="switch-alerts-enabled"
            />
          </div>
        </div>
      </div>

      {/* Email alerts */}
      <div className="border border-zinc-800 bg-zinc-950 mb-4">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-3">
          <Mail size={18} className="text-blue-400" />
          <div>
            <div className="font-display font-bold tracking-tight">E-Mail Alerts</div>
            <div className="text-xs text-zinc-500">Zone-Alerts per E-Mail mit Chart-Bild — direkt an deine Anmelde-Adresse.</div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3 px-3 py-2.5 bg-zinc-900 border border-zinc-800">
            <Mail size={13} className="text-zinc-500 shrink-0" />
            <span className="font-mono-num text-sm text-zinc-300">{userEmail || "—"}</span>
            <span className="text-[10px] text-zinc-600 ml-auto uppercase tracking-widest">Anmelde-Adresse</span>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Alternative Alert-Adresse</Label>
              <span className="text-[10px] text-zinc-600">Optional — überschreibt die Anmelde-Adresse</span>
            </div>
            <Input
              type="email"
              value={alertEmail}
              onChange={(e) => setAlertEmail(e.target.value)}
              placeholder={userEmail || "alerts@beispiel.de"}
              className="bg-zinc-900 border-zinc-800 font-mono-num text-sm"
            />
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Wenn keine Alternative eingetragen ist, werden Alerts an die Anmelde-Adresse gesendet.
          </p>
          <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
            <div>
              <div className="font-display font-bold text-sm">E-Mail Alerts aktivieren</div>
              <div className="text-xs text-zinc-500">Bei jedem Zone-Alert eine E-Mail mit Chart erhalten.</div>
            </div>
            <Switch checked={emailEnabled} onCheckedChange={setEmailEnabled} />
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="btn-save-settings"
          className="bg-white text-black hover:bg-zinc-200 rounded-sm font-display font-bold uppercase tracking-wider"
        >
          {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Speichern</> : <><Save size={14} className="mr-2" /> Speichern</>}
        </Button>
        <Button
          variant="outline"
          onClick={test}
          disabled={testing || !token || !chatId}
          data-testid="btn-test-telegram"
          className="border-zinc-700 bg-transparent hover:bg-zinc-800 rounded-sm font-display font-bold uppercase tracking-wider"
        >
          {testing ? <><Loader2 size={14} className="mr-2 animate-spin" /> Teste...</> : <><Send size={14} className="mr-2" /> Telegram Test</>}
        </Button>
        <Button
          variant="outline"
          onClick={testEmail}
          disabled={testingEmail}
          className="border-zinc-700 bg-transparent hover:bg-zinc-800 rounded-sm font-display font-bold uppercase tracking-wider"
        >
          {testingEmail ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sende...</> : <><Mail size={14} className="mr-2" /> E-Mail Test</>}
        </Button>
      </div>

      <div className="mt-10 border border-zinc-800 bg-zinc-950 p-5">
        <div className="font-display font-bold text-sm uppercase tracking-wider text-zinc-300 mb-2 flex items-center gap-2">
          <MessageSquare size={14} /> Anleitung
        </div>
        <ol className="text-xs text-zinc-400 space-y-2 list-decimal list-inside leading-relaxed">
          <li>Öffne Telegram und schreibe an <span className="text-emerald-400">@BotFather</span></li>
          <li>Sende <span className="font-mono-num text-white">/newbot</span> und folge den Anweisungen</li>
          <li>Kopiere den Bot-Token in das Feld oben</li>
          <li>Schreibe deinem neuen Bot einmal eine beliebige Nachricht (sonst kann er dich nicht erreichen)</li>
          <li>Hole dir deine Chat-ID via <span className="text-emerald-400">@userinfobot</span></li>
          <li>Klicke auf "Test senden" um die Verbindung zu prüfen</li>
        </ol>
      </div>

      {/* Password change */}
      <div className="mt-6 border border-zinc-800 bg-zinc-950">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-3">
          <KeyRound size={18} className="text-zinc-400" />
          <div>
            <div className="font-display font-bold tracking-tight">Passwort ändern</div>
            <div className="text-xs text-zinc-500">Lege ein neues Passwort für deinen Account fest.</div>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1 block">Neues Passwort</Label>
            <Input type="password" value={pw} onChange={e => setPw(e.target.value)}
              placeholder="Mindestens 6 Zeichen"
              className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1 block">Passwort bestätigen</Label>
            <Input type="password" value={pw2} onChange={e => setPw2(e.target.value)}
              placeholder="Wiederholen"
              className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
          <Button onClick={changePassword} disabled={changingPw}
            className="bg-white text-black hover:bg-zinc-200 rounded-sm font-display font-bold uppercase tracking-wider">
            {changingPw ? <><Loader2 size={14} className="mr-2 animate-spin" /> Ändern...</> : <><KeyRound size={14} className="mr-2" /> Passwort setzen</>}
          </Button>
        </div>
      </div>

      <EmailForwardSection />
    </div>
  );
}

function EmailForwardSection() {
  const inboundUrl = `${process.env.REACT_APP_BACKEND_URL}/api/inbound/email`;
  const copy = () => {
    navigator.clipboard.writeText(inboundUrl);
    toast.success("Inbound-URL kopiert.");
  };
  return (
    <div className="mt-6 border border-zinc-800 bg-zinc-950">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-3">
        <Mail size={18} className="text-emerald-500" />
        <div>
          <div className="font-display font-bold tracking-tight">E-Mail Forwarding (Auto-Setup)</div>
          <div className="text-xs text-zinc-500">
            Friedrich-Newsletter automatisch parsen — sobald eine E-Mail eingeht wird ein Setup angelegt.
          </div>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1 block">Webhook URL</Label>
          <div className="flex gap-2">
            <Input
              value={inboundUrl}
              readOnly
              className="bg-zinc-900 border-zinc-800 font-mono-num text-xs"
              data-testid="input-inbound-url"
            />
            <Button onClick={copy} variant="outline" className="border-zinc-700 bg-transparent hover:bg-zinc-800 shrink-0" data-testid="btn-copy-inbound-url">
              <Copy size={14} />
            </Button>
          </div>
        </div>

        <div className="text-xs text-zinc-400 space-y-3 leading-relaxed">
          <p className="text-zinc-300 font-display font-bold uppercase tracking-wider text-[11px]">Empfohlene Methode: Cloudflare Email Workers (gratis)</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-500">
            <li>Domain bei Cloudflare hinzufügen → "Email Routing" aktivieren</li>
            <li>Eine Adresse anlegen, z.B. <span className="font-mono-num text-emerald-400">friedrich@deinedomain.com</span></li>
            <li>Email Worker erstellen, der den Body extrahiert und per <span className="font-mono-num text-white">POST</span> an die Webhook-URL oben sendet</li>
            <li>Friedrich-Newsletter an diese Adresse weiterleiten (Gmail Filter / Outlook Regel)</li>
          </ol>

          <p className="text-zinc-300 font-display font-bold uppercase tracking-wider text-[11px] mt-4">Alternative: Mailgun / Postmark / SendGrid Inbound Parse</p>
          <p className="text-zinc-500">
            Diese Dienste empfangen E-Mails und senden sie als JSON-Webhook. Konfiguriere die Webhook-URL oben als Ziel.
          </p>

          <p className="text-zinc-300 font-display font-bold uppercase tracking-wider text-[11px] mt-4">Erwartetes JSON-Format</p>
          <pre className="bg-zinc-900 border border-zinc-800 p-3 text-[11px] font-mono-num overflow-x-auto text-zinc-400">{`{
  "from": "newsletter@friedrich.report",
  "subject": "Gold Setup",
  "text": "...vollständiger E-Mail Inhalt..."
}`}</pre>
          <p className="text-zinc-500 text-[11px]">
            Die Plattform analysiert den <span className="font-mono-num">text</span>-Body automatisch mit Claude Sonnet 4.5
            und legt ein Setup mit Zonen, Targets und Stop-Loss an.
          </p>
        </div>
      </div>
    </div>
  );
}
